import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FiPlus, FiTrash2, FiArrowLeft, FiLoader, FiSave } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import SearchableSelect from '../../components/common/SearchableSelect';
import Loader from '../../components/common/Loader';
import { today, unwrapList, formatMoney } from '../../utils/format';

const emptyItem = () => ({ description: '', quantity: 1, unit_price: 0, uom: '' });

export default function InvoiceForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const initialType = location.pathname.includes('/purchase') ? 'purchase' : 'sale';
  const [form, setForm] = useState({
    invoice_type: initialType,
    party_id: '',
    supplier_id: '',
    supplier_address: '',
    supplier_invoice_no: '',
    date: today(),
    tax_rate: 0,
    items: [emptyItem()],
    sales_contract: '',
    container_no: '',
    purpose: '',
    net_weight: '',
    commission_rate: '',
    usd_rate: '',
    total_pkr: '',
    invoice_amount_usd: '',
    hs_code: '',
  });
  const [parties, setParties] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [units, setUnits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [nextNo, setNextNo] = useState('');

  useEffect(() => {
    if (isEdit) return;
    api.invoices.nextNo({ invoice_type: form.invoice_type })
      .then(({ data }) => setNextNo(data.invoice_no || ''))
      .catch(() => setNextNo(''));
  }, [form.invoice_type, isEdit]);

  useEffect(() => {
    api.settings.get().then(({ data }) => {
      const raw = data.settings || data || {};
      const flat = {};
      if (typeof raw === 'object' && !Array.isArray(raw)) {
        Object.values(raw).forEach((group) => {
          if (Array.isArray(group)) group.forEach((s) => { flat[s.key] = s.value; });
        });
      }
      setForm((prev) => ({
        ...prev,
        tax_rate: Number(flat.invoice_tax_rate) || prev.tax_rate,
      }));
    }).catch(() => {});

    api.inventory.units().then(({ data }) => {
      const list = Array.isArray(data) ? data : data.units || [];
      setUnits(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.invoices.get(id).then(({ data }) => {
      const inv = data.invoice || data;
      const items = (inv.items && inv.items.length)
        ? inv.items.map((it) => ({ description: it.description || '', quantity: it.quantity, unit_price: it.unit_price, uom: it.uom || '' }))
        : [emptyItem()];
      setForm({
        invoice_type: inv.invoice_type || 'sale',
        party_id: inv.party_id || '',
        supplier_id: inv.supplier_id || '',
        supplier_address: inv.supplier_address || '',
        supplier_invoice_no: inv.supplier_invoice_no || '',
        date: inv.date ? String(inv.date).slice(0, 10) : today(),
        tax_rate: inv.tax_rate ?? 0,
        items,
        sales_contract: inv.sales_contract || '',
        container_no: inv.container_no || '',
        purpose: inv.purpose || '',
        net_weight: inv.net_weight ?? '',
        commission_rate: inv.commission_rate ?? (Number(inv.total_pkr) && Number(inv.net_weight) && Number(inv.usd_rate)
          ? String(Number((Number(inv.total_pkr) / (Number(inv.net_weight) * Number(inv.usd_rate))).toFixed(4)))
          : ''),
        usd_rate: inv.usd_rate ?? '',
        total_pkr: inv.total_pkr ?? '',
        invoice_amount_usd: inv.invoice_amount_usd ?? '',
        hs_code: inv.hs_code || '',
      });
    }).catch((err) => {
      showError('Failed to load invoice', extractError(err));
      navigate('/invoices');
    }).finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const isCommission = form.invoice_type === 'commission';
  const isPurchase = form.invoice_type === 'purchase';
  const isCredit = form.invoice_type === 'credit';
  const isTaxFree = form.invoice_type === 'tax_free';
  const isDebit = form.invoice_type === 'debit';
  const partyType = isPurchase || isDebit ? 'supplier' : 'customer';

  useEffect(() => {
    if (isPurchase || isDebit) {
      api.suppliers.list({ per_page: 500 }).then(({ data }) => {
        const raw = data.suppliers || data.data || data;
        const list = Array.isArray(raw) ? raw : raw?.data || [];
        setParties(list.map((s) => ({ value: s.id, label: s.name })));
      }).catch(() => {});
    } else {
      api.parties.list({ type: partyType, per_page: 500 }).then(({ data }) => {
        setParties(unwrapList(data, 'parties'));
      }).catch(() => {});
    }
  }, [partyType, isPurchase, isDebit]);

  useEffect(() => {
    if (!isCommission) return;
    api.suppliers.list({ per_page: 500 }).then(({ data }) => {
      const raw = data.suppliers || data.data || data;
      const list = Array.isArray(raw) ? raw : raw?.data || [];
      setSupplierOptions(list.map((s) => ({ value: s.id, label: s.name })));
    }).catch(() => {});
  }, [isCommission]);

  const subtotal = form.items.reduce((a, it) => a + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const taxRate = Number(form.tax_rate) || 0;
  const taxAmount = isTaxFree ? 0 : (subtotal * taxRate) / 100;
  const total = subtotal + taxAmount;

  // Commission: Total PKR = Net Weight x Commission Rate x USD Rate
  const calcTotalPkr = (nw, comm, roe) => (
    nw !== '' && comm !== '' && roe !== ''
    && !Number.isNaN(Number(nw)) && !Number.isNaN(Number(comm)) && !Number.isNaN(Number(roe))
  ) ? (Number(nw) * Number(comm) * Number(roe)).toFixed(2) : '';

  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: value };
    setForm({ ...form, items });
    if (errors.items) setErrors((p) => ({ ...p, items: '' }));
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.invoice_type) errs.invoice_type = 'Invoice type is required';
    if ((isPurchase || isDebit) && !form.supplier_id) errs.party_id = 'Please select a supplier';
    if (!isPurchase && !isDebit && !form.party_id) errs.party_id = 'Please select a party';
    if (!form.date) errs.date = 'Invoice date is required';
    if (form.invoice_type === 'purchase' && !form.supplier_invoice_no) errs.supplier_invoice_no = 'Supplier invoice number is required';

    if (isCommission) {
      if (!form.sales_contract) errs.sales_contract = 'Sales contract is required';
      if (!form.supplier_address) errs.supplier_address = 'Address is required';
      if (!form.net_weight) errs.net_weight = 'Net weight is required';
      if (!form.commission_rate) errs.commission_rate = 'Commission rate is required';
      if (!form.usd_rate) errs.usd_rate = 'USD rate is required';
      if (!form.invoice_amount_usd) errs.invoice_amount_usd = 'Invoice amount in USD is required';
    } else {
      if (!form.items.length) errs.items = 'At least one item is required';
      if (form.items.some((it) => !it.description || Number(it.quantity) <= 0 || Number(it.unit_price) < 0)) errs.items = 'Each item needs a description, a quantity greater than 0 and a valid unit price';
    }

    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const items = isCommission
      ? [{ description: form.purpose || 'Commission', quantity: 1, unit_price: Number(form.invoice_amount_usd) || 0 }]
      : form.items.filter((it) => it.description && Number(it.quantity) > 0);

    setSaving(true);
    try {
      const payload = {
        ...form,
        items,
        party_id: (isPurchase || isDebit) ? undefined : Number(form.party_id),
        supplier_id: (isPurchase || isDebit || isCommission) && form.supplier_id ? Number(form.supplier_id) : undefined,
        supplier_address: form.supplier_address ? form.supplier_address : undefined,
        supplier_invoice_no: form.supplier_invoice_no || undefined,
        total_pkr: form.total_pkr ? Number(form.total_pkr) : undefined,
        invoice_amount_usd: form.invoice_amount_usd ? Number(form.invoice_amount_usd) : undefined,
        net_weight: form.net_weight ? Number(form.net_weight) : undefined,
        commission_rate: form.commission_rate ? Number(form.commission_rate) : undefined,
        usd_rate: form.usd_rate ? Number(form.usd_rate) : undefined,
      };
      if (isEdit) {
        const { data } = await api.invoices.update(id, payload);
        toast('success', '', 'Invoice updated successfully');
        navigate(`/invoices/${data.invoice?.id || id}`);
      } else {
        const { data } = await api.invoices.create(payload);
        toast('success', '', 'Invoice created successfully');
        navigate(`/invoices/${data.invoice?.id || data.id}`);
      }
    } catch (err) {
      showError(isEdit ? 'Failed to update invoice' : 'Failed to create invoice', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader text="Loading invoice..." />;

  return (
    <div className="page">
      <PageHeader
        title={isEdit ? 'Edit Invoice' : 'New Invoice'}
        subtitle={isEdit ? 'Update the invoice details.' : 'Create a sales, credit, debit, unregistered or commission invoice.'}
        crumbs={[{ label: 'Finance', to: '/invoices' }, { label: 'Invoices', to: '/invoices' }, { label: isEdit ? 'Edit Invoice' : 'New Invoice' }]}
        actions={<button className="btn btn-secondary" onClick={() => navigate('/invoices')}><FiArrowLeft /> Back</button>}
      />

      {/* Invoice Type Toggle */}
      <div className="card">
        <div className="card-body">
          <div className="invoice-type-toggle">
            <label className={`toggle-label ${form.invoice_type === 'sale' ? 'active' : ''}`}>
              <input type="radio" name="invoice_mode" value="sale" checked={form.invoice_type === 'sale'} onChange={() => setForm({ ...form, invoice_type: 'sale' })} />
              <span className="toggle-text">
                <i className="bi bi-receipt" /> Sale Invoice (SI)
              </span>
            </label>
            <label className={`toggle-label ${isPurchase ? 'active' : ''}`}>
              <input type="radio" name="invoice_mode" value="purchase" checked={isPurchase} onChange={() => setForm({ ...form, invoice_type: 'purchase' })} />
              <span className="toggle-text">
                <i className="bi bi-cart-check" /> Purchase Invoice (PI)
              </span>
            </label>
            <label className={`toggle-label ${isCommission ? 'active' : ''}`}>
              <input type="radio" name="invoice_mode" value="commission" checked={isCommission} onChange={() => setForm({ ...form, invoice_type: 'commission' })} />
              <span className="toggle-text">
                <i className="bi bi-file-earmark-dollar" /> Commission Invoice (CM)
              </span>
            </label>
            <label className={`toggle-label ${isCredit ? 'active' : ''}`}>
              <input type="radio" name="invoice_mode" value="credit" checked={isCredit} onChange={() => setForm({ ...form, invoice_type: 'credit' })} />
              <span className="toggle-text">
                <i className="bi bi-arrow-counterclockwise" /> Credit Note (CR)
              </span>
            </label>
            <label className={`toggle-label ${isDebit ? 'active' : ''}`}>
              <input type="radio" name="invoice_mode" value="debit" checked={isDebit} onChange={() => setForm({ ...form, invoice_type: 'debit' })} />
              <span className="toggle-text">
                <i className="bi bi-arrow-clockwise" /> Debit Note (DN)
              </span>
            </label>
            <label className={`toggle-label ${isTaxFree ? 'active' : ''}`}>
              <input type="radio" name="invoice_mode" value="tax_free" checked={isTaxFree} onChange={() => setForm({ ...form, invoice_type: 'tax_free', tax_rate: 0 })} />
              <span className="toggle-text">
                <i className="bi bi-file-earmark-text" /> Unregistered (UR)
              </span>
            </label>
          </div>
          {!isEdit && !isPurchase && nextNo && (
            <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted)' }}>
              Next Invoice No: <b style={{ color: 'var(--primary)', fontSize: 14 }}>{nextNo}</b>
            </div>
          )}
        </div>
      </div>

      {!isCommission ? (
        /* ============ NORMAL / PURCHASE INVOICE FORM ============ */
        <>
          <div className="card" style={{ marginTop: 18 }}>
            <div className="card-body">
              <div className="grid-3">
                {isPurchase && (
                  <div className="form-group">
                    <label className="form-label">Supplier Invoice No <span className="req">*</span></label>
                    <input className={`form-control ${errors.supplier_invoice_no ? 'is-invalid' : ''}`} value={form.supplier_invoice_no} onChange={(e) => { setForm({ ...form, supplier_invoice_no: e.target.value }); if (errors.supplier_invoice_no) setErrors((p) => ({ ...p, supplier_invoice_no: '' })); }} placeholder="Invoice number from supplier's bill" />
                    {errors.supplier_invoice_no && <div className="invalid-feedback">{errors.supplier_invoice_no}</div>}
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">{isPurchase || isDebit ? 'Supplier' : isCredit ? 'Party (Credit Note)' : 'Party'} <span className="req">*</span></label>
                  <SearchableSelect
                    options={parties}
                    value={(isPurchase || isDebit) ? form.supplier_id : form.party_id}
                    onChange={(v) => {
                      if (isPurchase || isDebit) setForm({ ...form, supplier_id: v });
                      else setForm({ ...form, party_id: v });
                      if (errors.party_id) setErrors((p) => ({ ...p, party_id: '' }));
                    }}
                    isInvalid={!!errors.party_id}
                    placeholder={(isPurchase || isDebit) ? '-- Select supplier --' : '-- Select customer --'}
                  />
                  {errors.party_id && <div className="invalid-feedback">{errors.party_id}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Invoice Date <span className="req">*</span></label>
                  <input className={`form-control ${errors.date ? 'is-invalid' : ''}`} type="date" value={form.date} onChange={(e) => { setForm({ ...form, date: e.target.value }); if (errors.date) setErrors((p) => ({ ...p, date: '' })); }} />
                  {errors.date && <div className="invalid-feedback">{errors.date}</div>}
                </div>
                {!isTaxFree && (
                  <div className="form-group">
                    <label className="form-label">Tax Rate (%)</label>
                    <input className="form-control" type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} />
                  </div>
                )}
                {form.invoice_type === 'sale' && (
                  <div className="form-group">
                    <label className="form-label">HS Code</label>
                    <input className="form-control" type="text" value={form.hs_code || ''} onChange={(e) => setForm({ ...form, hs_code: e.target.value })} placeholder="HS Code" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 18 }}>
            <div className="card-header">
              <div className="card-title">Items</div>
              <button className="btn btn-secondary btn-sm" onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })}><FiPlus /> Add Item</button>
            </div>
            <div className="card-body">
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Description</th>
                      <th style={{ width: 90 }}>Qty</th>
                      <th style={{ width: 80 }}>UOM</th>
                      <th style={{ width: 130 }}>Unit Price</th>
                      <th className="table-num" style={{ width: 130 }}>Amount</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => (
                      <tr key={i}>
                        <td><input className={`form-control ${errors.items ? 'is-invalid' : ''}`} style={{ textTransform: 'uppercase' }} value={it.description} onChange={(e) => updateItem(i, 'description', e.target.value)} placeholder="Description" /></td>
                        <td><input className={`form-control ${errors.items ? 'is-invalid' : ''}`} type="number" value={it.quantity} onChange={(e) => updateItem(i, 'quantity', e.target.value)} /></td>
                        <td>
                          <select className="form-control" value={it.uom || ''} onChange={(e) => updateItem(i, 'uom', e.target.value)}>
                            <option value="">-- Select --</option>
                            {units.map((u) => <option key={u.id} value={u.short_name || u.name}>{u.short_name || u.name}</option>)}
                          </select>
                        </td>
                        <td><input className={`form-control ${errors.items ? 'is-invalid' : ''}`} type="number" value={it.unit_price} onChange={(e) => updateItem(i, 'unit_price', e.target.value)} /></td>
                        <td className="table-num" style={{ fontWeight: 600 }}>
                          {formatMoney((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}
                        </td>
                        <td>
                          <button className="btn-icon danger" onClick={() => { if (form.items.length === 1) return; setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) }); }}><FiTrash2 /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {errors.items && <div className="invalid-feedback">{errors.items}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <div style={{ width: 320 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Subtotal</span><b>{formatMoney(subtotal)}</b>
                  </div>
                  {!isTaxFree && !(isPurchase && taxRate === 0) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
                      <span style={{ color: 'var(--text-muted)' }}>GST Amount ({taxRate}%)</span><b>{formatMoney(taxAmount)}</b>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: 16, borderTop: '1px solid var(--border)' }}>
                    <b>Total</b><b style={{ color: 'var(--primary)' }}>{formatMoney(total)}</b>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* ============ COMMISSION INVOICE FORM ============ */
        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-header">
            <div className="card-title"><i className="bi bi-file-earmark-dollar" /> Commission Invoice</div>
          </div>
          <div className="card-body">
            <div className="grid-3">
              <div className="form-group">
                <label className="form-label">Date <span className="req">*</span></label>
                <input className={`form-control ${errors.date ? 'is-invalid' : ''}`} type="date" value={form.date} onChange={(e) => { setForm({ ...form, date: e.target.value }); if (errors.date) setErrors((p) => ({ ...p, date: '' })); }} />
                {errors.date && <div className="invalid-feedback">{errors.date}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Company <span className="req">*</span></label>
                <SearchableSelect options={parties} value={form.party_id} onChange={(v) => { setForm({ ...form, party_id: v }); if (errors.party_id) setErrors((p) => ({ ...p, party_id: '' })); }} isInvalid={!!errors.party_id} placeholder="-- Select company --" />
                {errors.party_id && <div className="invalid-feedback">{errors.party_id}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Supplier</label>
                <SearchableSelect
                  options={supplierOptions}
                  value={form.supplier_id}
                  onChange={(v) => setForm({ ...form, supplier_id: v })}
                  placeholder="-- Select supplier --"
                />
              </div>
            </div>

              <div className="form-group">
                <label className="form-label">Address <span className="req">*</span></label>
                <input className={`form-control ${errors.supplier_address ? 'is-invalid' : ''}`} value={form.supplier_address} onChange={(e) => { setForm({ ...form, supplier_address: e.target.value }); if (errors.supplier_address) setErrors((p) => ({ ...p, supplier_address: '' })); }} placeholder="Enter address manually" />
                {errors.supplier_address && <div className="invalid-feedback">{errors.supplier_address}</div>}
              </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Sales Contract <span className="req">*</span></label>
                <input className={`form-control ${errors.sales_contract ? 'is-invalid' : ''}`} value={form.sales_contract} onChange={(e) => { setForm({ ...form, sales_contract: e.target.value }); if (errors.sales_contract) setErrors((p) => ({ ...p, sales_contract: '' })); }} placeholder="Contract number" />
                {errors.sales_contract && <div className="invalid-feedback">{errors.sales_contract}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Container #</label>
                <input className="form-control" value={form.container_no} onChange={(e) => setForm({ ...form, container_no: e.target.value })} placeholder="Container number" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description / Purpose</label>
              <input className="form-control" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Description of goods / services" />
            </div>

            <div className="form-group">
              <label className="form-label">Net Weight (Kg) <span className="req">*</span></label>
              <input className={`form-control ${errors.net_weight ? 'is-invalid' : ''}`} type="number" step="0.01" value={form.net_weight} onChange={(e) => {
                const nw = e.target.value;
                setForm({ ...form, net_weight: nw, total_pkr: calcTotalPkr(nw, form.commission_rate, form.usd_rate) });
                if (errors.net_weight) setErrors((p) => ({ ...p, net_weight: '' }));
              }} placeholder="Net weight in kg" />
              {errors.net_weight && <div className="invalid-feedback">{errors.net_weight}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Commission Rate <span className="req">*</span></label>
              <input className={`form-control ${errors.commission_rate ? 'is-invalid' : ''}`} type="number" step="0.0001" value={form.commission_rate} onChange={(e) => {
                const comm = e.target.value;
                setForm({ ...form, commission_rate: comm, total_pkr: calcTotalPkr(form.net_weight, comm, form.usd_rate) });
                if (errors.commission_rate) setErrors((p) => ({ ...p, commission_rate: '' }));
              }} placeholder="e.g. 0.10" />
              {errors.commission_rate && <div className="invalid-feedback">{errors.commission_rate}</div>}
            </div>

            <div className="grid-3">
              <div className="form-group">
                <label className="form-label">Invoice Amount (US$) <span className="req">*</span></label>
                <input className={`form-control ${errors.invoice_amount_usd ? 'is-invalid' : ''}`} type="number" step="0.01" value={form.invoice_amount_usd} onChange={(e) => {
                  setForm({ ...form, invoice_amount_usd: e.target.value });
                  if (errors.invoice_amount_usd) setErrors((p) => ({ ...p, invoice_amount_usd: '' }));
                }} placeholder="Amount in USD" />
                {errors.invoice_amount_usd && <div className="invalid-feedback">{errors.invoice_amount_usd}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">USD Rate <span className="req">*</span></label>
                <input className={`form-control ${errors.usd_rate ? 'is-invalid' : ''}`} type="number" step="0.0001" value={form.usd_rate} onChange={(e) => {
                  const roe = e.target.value;
                  setForm({ ...form, usd_rate: roe, total_pkr: calcTotalPkr(form.net_weight, form.commission_rate, roe) });
                  if (errors.usd_rate) setErrors((p) => ({ ...p, usd_rate: '' }));
                }} placeholder="e.g. 278.50" />
                {errors.usd_rate && <div className="invalid-feedback">{errors.usd_rate}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Total PKR</label>
                <input className="form-control" type="number" value={form.total_pkr} readOnly placeholder="Auto-calculated" style={{ backgroundColor: '#f5f5f5', fontWeight: 600 }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
        <button className="btn btn-secondary" onClick={() => navigate('/invoices')}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <><FiLoader /> Saving...</> : <><FiSave /> {isEdit ? 'Update Invoice' : 'Create Invoice'}</>}
        </button>
      </div>
    </div>
  );
}
